import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormRoot,
  toast,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft, HelpCircle, User } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import * as RHF from "react-hook-form";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import type { CreateLeaveRequestDTO } from "@/modules/hr/leave/domain/types";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { config } from "@/shared/lib/config";
import {
  fetchLookupForInfinite,
  hydrateLookupItem,
  type LookupItem,
} from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import {
  useCreateLeaveRequestMutation,
  useLeaveEntitlementsQuery,
} from "../api/queries";
import {
  LeaveRequestFormTour,
  useLeaveRequestFormTour,
} from "../tour/LeaveRequestFormTour";
import { LeaveRequestForm } from "../ui/LeaveRequestForm";
import { getLeaveRequestCreateFormSamples } from "../ui/leaveRequestCreateFormSamples";

const isDev =
  typeof import.meta !== "undefined" &&
  (import.meta.hot !== undefined || import.meta.env?.MODE === "development");

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;
const LIST_SEARCH = {
  offset: 0,
  limit: 20,
  sort: undefined as { field: string; dir: "asc" | "desc" }[] | undefined,
  periodYear: currentYear,
  periodMonth: currentMonth,
  periodFromOnly: true,
  filterByCreated: true,
  status: undefined as string | undefined,
  search: undefined as string | undefined,
};

const HISTORY_BACK_SEARCH = {
  offset: 0,
  limit: 20,
  sort: undefined as { field: string; dir: "asc" | "desc" }[] | undefined,
  periodYear: currentYear,
  periodMonth: currentMonth,
  periodFromOnly: false,
  status: undefined as string | undefined,
  search: undefined as string | undefined,
};

type CreateFormValues = {
  employeeId: string;
};

export function LeaveRequestCreatePage() {
  const nav = useNavigate();
  const { hasAny } = usePermissions();
  const createMutation = useCreateLeaveRequestMutation();
  const tour = useLeaveRequestFormTour();
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>("");

  const entitlementsQuery = useLeaveEntitlementsQuery(
    {
      employeeId: selectedEmployeeId || undefined,
      year: currentYear,
      limit: 100,
    },
    { enabled: !!selectedEmployeeId },
  );

  // Leave type options only from this employee's entitlements (available days from leaveEntitlement)
  const leaveTypeOptions = useMemo(() => {
    const entitlements = entitlementsQuery.data?.data ?? [];
    return entitlements.map((e) => ({
      value: e.leaveTypeId,
      label: `${e.leaveTypeName} (${e.leaveTypeCode}) — ຄົງເຫຼືອ ${e.remainingDays} ວັນ`,
    }));
  }, [entitlementsQuery.data?.data]);

  const employeeMethods = RHF.useForm<CreateFormValues>({
    defaultValues: { employeeId: "" },
  });
  const watchedEmployeeId = employeeMethods.watch("employeeId");
  useEffect(() => {
    setSelectedEmployeeId(watchedEmployeeId?.trim() ?? "");
  }, [watchedEmployeeId]);

  const handleBack = () => {
    if (
      hasAny([
        Permissions.hr_leave.approve,
        Permissions.hr_leave.approve_line,
      ])
    ) {
      nav({ to: "/app/hr/leave/approvals", search: LIST_SEARCH });
    } else if (hasAny([Permissions.hr_leave.history])) {
      nav({ to: "/app/hr/leave/history", search: HISTORY_BACK_SEARCH });
    } else {
      nav({ to: "/app/dashboard" });
    }
  };

  const handleSubmit = (values: Omit<CreateLeaveRequestDTO, "employeeId">) => {
    const employeeId =
      selectedEmployeeId || employeeMethods.getValues("employeeId")?.trim();
    if (!employeeId) {
      toast.error("ກະລຸນາເລືອກພະນັກງານ");
      return;
    }
    createMutation.mutate(
      { employeeId, ...values },
      {
        onSuccess: (data) => {
          toast.success("ສົ່ງຄຳຂໍລາສຳເລັດ");
          // Use period from the created request's startDate so "Back" to approvals shows this row (list filters by periodYear/periodMonth)
          const startStr = values.startDate.slice(0, 10);
          const periodYear = new Date(startStr).getFullYear();
          const periodMonth = new Date(startStr).getMonth() + 1;
          const fromFlow = hasAny([
            Permissions.hr_leave.approve,
            Permissions.hr_leave.approve_line,
          ])
            ? "approvals"
            : hasAny([Permissions.hr_leave.history])
              ? "history"
              : "create";
          nav({
            to: "/app/hr/leave/requests/$id",
            params: { id: data.id },
            search: {
              from: fromFlow,
              offset: 0,
              limit: 20,
              periodYear,
              periodMonth,
              status: undefined,
              search: undefined,
            },
          });
        },
        onError: (err: { message?: string }) => {
          toast.error(err?.message ?? "ເກີດຂໍ້ຜິດພາດ");
        },
      },
    );
  };

  return (
    <>
      <Header />
      <Main>
        <LeaveRequestFormTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div
          className="mb-6 flex items-center justify-between gap-2"
          data-tourid="leave-request-form-back"
        >
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-semibold text-2xl">ຟອມຄຳຂໍລາ</h1>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 rounded-full"
                onClick={tour.startTour}
                title="ເລີ່ມທົດສອບການນຳທາງ"
                aria-label="ເລີ່ມທົດສອບການນຳທາງ"
              >
                <HelpCircle className="size-5" />
              </Button>
            </div>
            <p className="text-muted-foreground text-sm">
              ເລືອກພະນັກງານ ປະເພດລາ ວັນເລີ່ມ–ສິ້ນສຸດ ເຫດຜົນ; ລະບົບຄິດວັນລາອັດຕະໂນມັດ
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            ກັບໄປລາຍການ
          </Button>
        </div>

        <div
          className="mx-auto w-full max-w-2xl space-y-6"
          data-tourid="leave-request-form-form"
        >
          <FormRoot<CreateFormValues>
            methods={employeeMethods}
            onSubmit={() => {}}
            className="w-full"
          >
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <User className="h-4 w-4" />
                  ພະນັກງານ <span className="text-destructive">*</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <FormInfiniteCombobox<LookupItem>
                  name="employeeId"
                  label="ເລືອກພະນັກງານ"
                  requiredMark
                  queryKey={["users-lookup-leave-request-create"]}
                  queryFn={(args) =>
                    fetchLookupForInfinite(
                      `${config.apiUrl}/users/lookup`,
                      args,
                    )
                  }
                  preloadQueryFn={(id) =>
                    hydrateLookupItem(`${config.apiUrl}/users/lookup`, id)
                  }
                  getLabel={(item) => item.name}
                  getValue={(item) => item.id}
                  placeholder="ເລືອກພະນັກງານ..."
                />
              </CardContent>
            </Card>
          </FormRoot>

          <LeaveRequestForm
            mode="create"
            leaveTypeOptions={leaveTypeOptions}
            selectedEmployeeId={selectedEmployeeId}
            onSubmit={handleSubmit}
            onCancel={handleBack}
            isSubmitting={createMutation.isPending}
            autoFillGetSamples={
              isDev ? getLeaveRequestCreateFormSamples : undefined
            }
          />
        </div>
      </Main>
    </>
  );
}
